import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
interface LoadingAnimationProps {
  onComplete: () => void;
}
const WINDOW_EASE = [0.76, 0, 0.24, 1] as const;
const ZOOM_EASE = [0.25, 0.46, 0.45, 0.94] as const;
const SCREW_STYLE: React.CSSProperties = {
  width: 12,
  height: 12,
  borderRadius: '50%',
  background: 'radial-gradient(circle at 35% 35%, #888888 0%, #555555 50%, #333333 100%)',
  border: '1px solid #666666',
  boxShadow: '0 1px 3px rgba(0,0,0,0.5)'
};
const CLOSE_BAR_STYLE: React.CSSProperties = {
  width: '90%',
  height: 4,
  background: 'linear-gradient(90deg, #3A3A3A 0%, #606060 30%, #707070 50%, #606060 70%, #3A3A3A 100%)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.5)'
};
/**
 * Cinematic satin-aluminium trailer loader.
 * Timeline (from mount):
 *  0.0s — full brushed-aluminium screen, closed window w/ screws + handle
 *  1.0s — window slides up; warm interior light grows
 *  2.6s — whole scene zooms in (scale 5) and the aluminium fades out
 *  3.5s — black overlay fades in
 *  3.8s — Hero fades in underneath; overlay unmounts ~4.6s
 */
export function LoadingAnimation({
  onComplete
}: LoadingAnimationProps) {
  const [opening, setOpening] = useState(false);
  const [zooming, setZooming] = useState(false);
  const [blackout, setBlackout] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    const openTimer = setTimeout(() => setOpening(true), 1000);
    const zoomTimer = setTimeout(() => setZooming(true), 2600);
    const blackTimer = setTimeout(() => setBlackout(true), 3500);
    const doneTimer = setTimeout(() => onComplete(), 4600);
    return () => {
      clearTimeout(openTimer);
      clearTimeout(zoomTimer);
      clearTimeout(blackTimer);
      clearTimeout(doneTimer);
    };
  }, [onComplete]);
  return <div className="fixed inset-0 z-[9999] overflow-hidden" style={{
    pointerEvents: 'none'
  }} data-id="element-76" data-mp="2zb49fkntvaia">
      {/* Satin-aluminium scene that zooms in and fades out */}
      <motion.div className="absolute inset-0 flex items-center justify-center" style={{
      backgroundColor: '#8A8A8A',
      backgroundImage: [
      // panel seam lines
      'repeating-linear-gradient(90deg, transparent 0px, transparent 120px, rgba(0,0,0,0.08) 120px, rgba(0,0,0,0.08) 122px, transparent 122px)',
      // directional satin streaks
      'repeating-linear-gradient(75deg, transparent 0px, transparent 40px, rgba(255,255,255,0.04) 40px, rgba(255,255,255,0.04) 41px, transparent 41px, transparent 80px)',
      // fine brushed texture
      'repeating-linear-gradient(90deg, rgba(255,255,255,0.07) 0px, rgba(255,255,255,0.07) 1px, transparent 1px, transparent 5px)', 'repeating-linear-gradient(180deg, rgba(0,0,0,0.04) 0px, rgba(0,0,0,0.04) 1px, transparent 1px, transparent 5px)',
      // base brushed-metal gradient
      'linear-gradient(135deg, #9A9A9A 0%, #7A7A7A 25%, #8E8E8E 50%, #707070 75%, #888888 100%)'].join(', ')
    }} animate={{
      scale: zooming ? isMobile ? 3 : 5 : 1,
      opacity: zooming ? 0 : 1
    }} transition={{
      scale: {
        duration: 1.4,
        ease: ZOOM_EASE
      },
      opacity: {
        duration: 0.5,
        delay: 0.9,
        ease: 'easeOut'
      }
    }} data-id="element-77" data-mp="be5ukdl35kw8">
        <div className="relative flex flex-col items-center" data-id="element-78" data-mp="1z7ho9652izte">
          {/* Window frame (clips the rising panel) */}
          <div className="relative overflow-hidden" style={{
          width: isMobile ? '85vw' : 480,
          height: isMobile ? 200 : 300,
          border: '4px solid #5A5A5A',
          borderRadius: '4px 4px 0 0',
          background: '#1A1A1A',
          boxShadow: '0 0 0 1px #707070, 0 0 0 2px #3A3A3A, inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.3), 0 8px 32px rgba(0,0,0,0.6)'
        }} data-id="element-79" data-mp="1pflj5b4ke9fo">
            {/* Warm interior light revealed as the window rises */}
            <motion.div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at center bottom, rgba(216,197,160,0.3) 0%, rgba(216,197,160,0.15) 35%, rgba(180,140,80,0.06) 60%, transparent 80%)',
            backgroundColor: '#141109'
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: opening ? 1 : 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} data-id="element-80" data-mp="u9ntq9nbq8iz">
              <motion.span className="absolute inset-0 flex items-center justify-center font-display text-bp-accent tracking-[0.3em] uppercase pl-[0.3em] text-2xl md:text-3xl" initial={{
              opacity: 0
            }} animate={{
              opacity: opening ? 0.55 : 0
            }} transition={{
              duration: 0.8,
              delay: 0.3
            }} data-id="element-81" data-mp="20nn3bepvuhsj">
                BAJO PERFIL
              </motion.span>
            </motion.div>

            {/* The aluminium window panel that lifts up */}
            <motion.div className="absolute inset-0 flex flex-col items-center justify-center" style={{
            background: 'linear-gradient(180deg, #2E2E2E 0%, #222222 50%, #1A1A1A 100%)'
          }} initial={{
            y: 0
          }} animate={{
            y: opening ? '-110%' : 0
          }} transition={{
            duration: 1.6,
            ease: WINDOW_EASE
          }} data-id="element-82" data-mp="2m7p9jzc5xnnv">
              {/* satin sheen */}
              <div className="absolute inset-0 pointer-events-none" style={{
              background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.05) 50%, transparent 65%)'
            }} data-id="element-83" data-mp="1arrp35ouuybz" />

              {/* close bars at upper & lower thirds */}
              <div className="absolute left-1/2 -translate-x-1/2" style={{
              top: '33%',
              ...CLOSE_BAR_STYLE
            }} data-id="element-84" data-mp="1esag4xixc0d4" />
              <div className="absolute left-1/2 -translate-x-1/2" style={{
              top: '66%',
              ...CLOSE_BAR_STYLE
            }} data-id="element-85" data-mp="1oid94xq1pebg" />

              {/* handle */}
              <div className="absolute left-1/2 -translate-x-1/2" style={{
              bottom: 16,
              width: 70,
              height: 10,
              borderRadius: 5,
              background: 'linear-gradient(180deg, #707070 0%, #505050 100%)',
              border: '1px solid #808080',
              boxShadow: '0 2px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            }} data-id="element-86" data-mp="1yfhuivc2h5r7" />

              {/* corner screws */}
              <div className="absolute" style={{
              top: 8,
              left: 8,
              ...SCREW_STYLE
            }} data-id="element-87" data-mp="3lchga1g57v0k" />
              <div className="absolute" style={{
              top: 8,
              right: 8,
              ...SCREW_STYLE
            }} data-id="element-88" data-mp="3bh5zzep36fqp" />
              <div className="absolute" style={{
              bottom: 8,
              left: 8,
              ...SCREW_STYLE
            }} data-id="element-89" data-mp="32fg867ralese" />
              <div className="absolute" style={{
              bottom: 8,
              right: 8,
              ...SCREW_STYLE
            }} data-id="element-90" data-mp="10lqp7gmotgok" />
            </motion.div>
          </div>

          {/* Counter / mostrador */}
          <div style={{
          height: 32,
          width: isMobile ? '92vw' : 520,
          background: 'linear-gradient(180deg, #707070 0%, #555555 40%, #404040 100%)',
          borderTop: '2px solid #808080',
          borderBottom: '2px solid #2A2A2A',
          boxShadow: '0 6px 20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.1)'
        }} data-id="element-91" data-mp="1zgw450q0se9i" />
        </div>
      </motion.div>

      {/* Black overlay that fades in as the zoom completes, bridging to the carta */}
      <motion.div className="absolute inset-0" style={{
      backgroundColor: '#0A0A0A'
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: blackout ? 1 : 0
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} data-id="element-92" data-mp="3axw0c7fhnd8t" />
    </div>;
}